// Deep import (not the @libs/Reports barrel) keeps ReportsRoute lazy-loaded.
import { DEFAULT_REPORT_WINDOW_PRESET } from "@libs/Reports/utils/reportPresets";
import { DEFAULT_MANAGE_SETTINGS_PATH } from "@libs/Settings/settingsNavigation";
import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  type LoaderFunctionArgs,
  Navigate,
  Route,
  redirect,
} from "react-router";
import { buildCompanyPath } from "@/hooks/companyPath";
import { safeConsole } from "@/utils/safeConsole";
import { AuthErrorBoundary } from "./AuthErrorBoundary";
import { authLoader } from "./authLoader";
import { CompanyNavigate } from "./CompanyNavigate";
import { isChunkLoadError } from "./chunkLoadRecovery";
import { companyRootRedirect, companySlugLoader } from "./companySlugLoader";
import { legacyAgentPersonalityRedirect } from "./legacyAgentPersonalityRedirect";
import {
  OperatorAutomationRedirect,
  OperatorHomeRedirect,
  OperatorRoutinesRedirect,
  OperatorScheduledViewRedirect,
  OperatorTaskRedirect,
} from "./OperatorRedirects";
import { loginRouteManifest } from "./publicRouteManifest";
import { RootErrorBoundary } from "./RootErrorBoundary";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import { isRouteComponentExport } from "./routeComponentExport";
import { companyDataLoader } from "./routeLoaders";
import { SearchParamKeyedRoute } from "./SearchParamKeyedRoute";
import { SUPER_PATHS } from "./superRoutes";
import type { RouteHandle } from "./types";

const fullWidth: RouteHandle = { layout: "fullWidth" };

// Loader for legacy splat routes: forwards the remainder of the path into a
// new namespace (e.g. /company/hours → /settings/hours), keeping the query
// string so deep links survive the move.
const redirectSplat =
  (base: string) =>
  ({ params, request }: LoaderFunctionArgs) =>
    redirect(
      buildCompanyPath(
        params.slug,
        `${base}/${params["*"] ?? ""}${new URL(request.url).search}`,
      ),
    );

const redirectWithinCompany =
  (to: string) =>
  ({ params }: LoaderFunctionArgs) =>
    redirect(buildCompanyPath(params.slug, to));

const redirectGlobal =
  (to: string) =>
  ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    return redirect(to + url.search + url.hash);
  };

// Compliance's former section query is still used by external entry points.
// Redirect the moved sections to their canonical routes; recording remains the
// default page at /phone/compliance.
const redirectLegacyPhoneComplianceSection = ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const section = new URL(request.url).searchParams.get("section");
  let destination: string | undefined;

  switch (section) {
    case "inbound":
      destination = "/settings/inbound-consent";
      break;
    case "outbound":
      destination = "/settings/outbound-consent";
      break;
    case "texting":
      // Straight to Settings (SD-15879) rather than bouncing through the
      // legacy Phone alias, which only redirects here again.
      destination = "/settings/texting-registration";
      break;
  }

  return destination
    ? redirect(buildCompanyPath(params.slug, destination))
    : undefined;
};

// The module type remains specific to each dynamic import, so every requested
// export name is compiler-checked. The runtime guard still protects a stale or
// malformed chunk from becoming a route component.
const lazyImport = <TModule extends object>(
  importFn: () => Promise<TModule>,
  componentName: Extract<keyof TModule, string>,
  options?: { keySearchParam?: string },
) => {
  const resolveComponent = (module: TModule) => {
    const exported = module[componentName];
    if (!isRouteComponentExport(exported)) {
      throw new Error(
        `Route module export ${componentName} is not a component`,
      );
    }
    const Component = exported;

    const keySearchParam = options?.keySearchParam;
    if (!keySearchParam) {
      return { Component };
    }

    return {
      element: (
        <SearchParamKeyedRoute
          component={Component}
          paramName={keySearchParam}
        />
      ),
    };
  };

  return async () => {
    try {
      return resolveComponent(await importFn());
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);

      // Stale/transient code-split chunk: retry the import once. A flaky network
      // or a service-worker stale-cache race usually recovers here silently — no
      // reload, no error flash, no lost click.
      if (isChunkLoadError(message)) {
        try {
          return resolveComponent(await importFn());
        } catch (retryError) {
          // Still failing (genuinely stale chunk after a deploy). Throw rather
          // than reload: react-router then commits the navigation to the target
          // URL, and RouteErrorBoundary/RootErrorBoundary reload to fetch fresh
          // chunks — landing on the page the user clicked, not the previous one.
          safeConsole.error(
            "route-loading",
            `Failed to load module ${componentName} after retry:`,
            retryError,
          );
          throw retryError;
        }
      }

      safeConsole.error(
        "route-loading",
        `Failed to load module ${componentName}:`,
        error,
      );
      throw error;
    }
  };
};

// Sentry-instrumented router factory: names pageload/navigation transactions
// after the parameterized route (e.g. /agents/:id) instead of the raw URL.
// Requires Sentry.init (src/instrument.ts) to have run before this module.
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// The authenticated route subtree is mounted unprefixed for global and
// transitional localStorage-scoped routes, and under `/:slug` for canonical
// company routes. A function so each mount gets its own element tree rather
// than sharing one instance.
const renderAuthenticatedRoutes = () => (
  <Route errorElement={<RouteErrorBoundary />}>
    <Route path="agents">
      <Route path="receptionist" loader={redirectWithinCompany("/agents")} />
      <Route
        path="customer-service"
        loader={redirectWithinCompany("/agents")}
      />
      <Route path="dispatcher" loader={redirectWithinCompany("/agents")} />
      <Route
        path="playbooks"
        lazy={lazyImport(
          () => import("@libs/Playbooks/PlaybooksPage"),
          "PlaybooksPage",
        )}
      />
      <Route
        path="workflows"
        handle={fullWidth}
        lazy={lazyImport(() => import("@libs/Workflows"), "WorkflowEditor")}
      />
      <Route
        handle={fullWidth}
        lazy={lazyImport(() => import("@libs/Agents"), "AgentsLayout")}
      >
        <Route
          index
          lazy={lazyImport(() => import("@libs/Agents"), "AgentsIndexRedirect")}
        />
        {/* Moved to Settings -> AI Transfer Destinations; the Agents URL
            shipped in v2.138.0, so keep it redirecting. */}
        <Route
          path="transfer-destinations"
          element={
            <CompanyNavigate to="/settings/transfer-destinations" replace />
          }
        />
        {/* /agents/new falls through to /agents/:id with id="new", rendering the wizard */}
        <Route
          path=":id"
          lazy={lazyImport(() => import("@libs/Agents"), "AgentEditor")}
        >
          <Route
            index
            loader={({ params }) =>
              redirect(
                buildCompanyPath(
                  params.slug,
                  `/agents/${params.id}/call-logic`,
                ),
              )
            }
          />
          <Route
            path="greeting"
            lazy={lazyImport(() => import("@libs/Agents"), "GreetingContainer")}
          />
          <Route
            path="tool-messages"
            lazy={lazyImport(
              () => import("@libs/Agents"),
              "ToolCallMessagesContainer",
            )}
          />
          <Route
            path="voices"
            lazy={lazyImport(() => import("@libs/Agents"), "VoicesContainer")}
          />
          <Route
            path="call-logic"
            lazy={lazyImport(() => import("@libs/Agents"), "CallLogicEditor")}
          />
          <Route
            path="playbook"
            lazy={lazyImport(
              () => import("@libs/Agents"),
              "PlaybookAssignmentContainer",
            )}
          />
          <Route
            path="advanced-settings"
            lazy={lazyImport(
              () => import("@libs/Agents"),
              "AdvancedSettingsContainer",
            )}
          />
        </Route>
      </Route>
    </Route>
    {/* Legacy routes: redirect to canonical /agents/new and /agents/:id */}
    <Route
      path="agents/:id/personality"
      loader={legacyAgentPersonalityRedirect}
    />
    <Route
      path="agents/new-full/*"
      loader={({ params, request }) => {
        const url = new URL(request.url);
        return redirect(
          buildCompanyPath(params.slug, `/agents/new${url.search}`),
        );
      }}
    />
    <Route
      path="agents/full/:id/*"
      loader={({ params }) =>
        redirect(buildCompanyPath(params.slug, `/agents/${params.id}`))
      }
    />
    {/* Super configuration hub. Company-scoped tools carry the active slug;
        global configuration tools remain unprefixed. */}
    <Route
      path="super"
      lazy={lazyImport(() => import("@libs/Super"), "SuperLayout")}
    >
      <Route
        index
        lazy={lazyImport(
          () => import("@libs/CompanySelection/CompanySelection"),
          "CompanySelection",
        )}
      />
      <Route
        path="accounts"
        lazy={lazyImport(() => import("@libs/Super"), "AccountsPage")}
      />
      <Route
        path="accounts/:accountId/billing"
        lazy={lazyImport(() => import("@libs/Account"), "AccountBillingPage")}
      />
      <Route
        path="quotes"
        lazy={lazyImport(() => import("@libs/Super"), "QuotesPage")}
      />
      <Route
        path="onboarding-calendar"
        lazy={lazyImport(
          () => import("@libs/Super/QuoteOnboardingCalendarPage"),
          "QuoteOnboardingCalendarPage",
        )}
      />
      <Route
        path="demo-identities"
        lazy={lazyImport(() => import("@libs/Super"), "DemoIdentitiesPage")}
      />
      <Route
        path="playbooks"
        lazy={lazyImport(
          () => import("@libs/Playbooks/PlaybooksPage"),
          "PlaybooksPage",
        )}
      />
      <Route path="agent-templates">
        <Route
          index
          lazy={lazyImport(
            () => import("@libs/AgentTemplates"),
            "AgentTemplatesPage",
          )}
        />
        <Route
          path=":agentId"
          lazy={lazyImport(
            () => import("@libs/AgentTemplates"),
            "AgentTemplateEditor",
          )}
        />
      </Route>
      {/* Per-company staff settings use only their /super namespace. */}
      <Route
        path="company/expansion"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyExpansion")}
      />
      <Route
        path="company/voice-ai-provider"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyVoiceAiProvider",
        )}
      />
      <Route
        path="company/legacy-voice-controls"
        lazy={lazyImport(
          () => import("@libs/Company/CompanyLegacyVoiceControls"),
          "CompanyLegacyVoiceControls",
        )}
      />
      <Route
        path="company/sensitive-controls"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanySensitiveControls",
        )}
      />
      <Route
        path="company/in-development"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyInDevelopment")}
      />
      <Route
        path="company/internal-connections"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyInternalConnections",
        )}
      />
      <Route
        path="insights"
        lazy={lazyImport(() => import("@libs/Insights"), "Insights")}
      />
      {/* Industry Insights moved into the Insights tab bar; keep the old URL
          working by redirecting to the tab. */}
      <Route
        path="industry-insights"
        element={
          <Navigate to={`${SUPER_PATHS.insights}?view=industry`} replace />
        }
      />
      <Route
        path="marketing-website"
        lazy={lazyImport(
          () => import("@libs/MarketingWebsite"),
          "MarketingWebsitePage",
        )}
      />
      <Route path="manage-settings">
        <Route index loader={() => redirect(DEFAULT_MANAGE_SETTINGS_PATH)} />
        <Route
          path=":settingPath"
          lazy={lazyImport(() => import("@libs/Settings/Settings"), "Settings")}
        />
      </Route>
      <Route
        path="monitoring"
        lazy={lazyImport(() => import("@libs/Monitoring"), "Monitoring")}
      >
        <Route
          index
          lazy={lazyImport(
            () => import("@libs/Monitoring"),
            "MonitoringDashboards",
          )}
        />
      </Route>
      <Route
        path="disposition-backfill"
        lazy={lazyImport(
          () => import("@libs/Super"),
          "DispositionBackfillPage",
        )}
      />
      <Route
        path="report-email-preview"
        lazy={lazyImport(() => import("@libs/Super"), "ReportEmailPreviewPage")}
      />
      <Route
        path="semantic-search"
        lazy={lazyImport(
          () => import("@libs/SemanticSearch/SemanticSearch"),
          "SemanticSearch",
        )}
      >
        <Route
          index
          lazy={lazyImport(
            () => import("@libs/SemanticSearch/SemanticSearchPage"),
            "SemanticSearchPage",
          )}
        />
      </Route>
    </Route>
    {/* Search Console's server callback and pre-Super bookmarks used this
        path. Keep it as a redirect so both land on the canonical admin URL. */}
    <Route
      path="marketing-website"
      loader={redirectGlobal(SUPER_PATHS.marketingWebsite)}
    />
    {/* Shared destinations keep their customer-facing paths. Super users reach
                the same pages through /super, where the staff sidebar is present. */}
    <Route
      path="company-selection"
      lazy={lazyImport(
        () => import("@libs/CompanySelection/CompanySelection"),
        "CompanySelection",
      )}
    />
    <Route
      path="monitoring"
      lazy={lazyImport(() => import("@libs/Monitoring"), "Monitoring")}
    >
      <Route
        index
        lazy={lazyImport(
          () => import("@libs/Monitoring"),
          "MonitoringDashboards",
        )}
      />
    </Route>
    <Route
      path="semantic-search"
      lazy={lazyImport(
        () => import("@libs/SemanticSearch/SemanticSearch"),
        "SemanticSearch",
      )}
    >
      <Route
        index
        lazy={lazyImport(
          () => import("@libs/SemanticSearch/SemanticSearchPage"),
          "SemanticSearchPage",
        )}
      />
    </Route>
    {/* Settings workspace: the merged Company + Account sections. The
                shell (left sidebar) lives in @libs/Company. */}
    <Route
      path="settings"
      lazy={lazyImport(() => import("@libs/Company"), "default")}
    >
      <Route
        index
        lazy={lazyImport(() => import("@libs/Company"), "CompanyInfo")}
      />
      <Route
        path="hours"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyHours")}
      />
      <Route
        path="holidays"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyHolidays")}
      />
      <Route
        path="service-areas"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyServiceAreas")}
      />
      {/* Priorities retired — the tab is gone, but the URL keeps working.
                  There is no splat under `settings`, so a bare removal would
                  404 into the root error boundary instead. */}
      <Route path="priorities" loader={redirectWithinCompany("/settings")} />
      <Route
        path="custom-analysis"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyCustomAnalysis",
        )}
      />
      <Route
        path="transfer-destinations"
        lazy={lazyImport(() => import("@libs/Company"), "TransferringNumbers")}
      />
      {/* Moved from the retired standalone Services nav (SD-10920);
                  the components still live in @libs/Services. */}
      <Route
        path="custom-services"
        lazy={lazyImport(() => import("@libs/Services"), "ServicesPage")}
      />
      <Route
        path="pricing-lookup"
        lazy={lazyImport(() => import("@libs/Services"), "PricingLookup")}
      />
      <Route
        path="services-not-offered"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyServicesNotOffered",
        )}
      />
      <Route
        path="faqs"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyFaqs")}
      />
      <Route
        path="reports"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyReportsSettings",
        )}
      />
      {/* The former Settings compliance page now remains at Phone → Compliance
                  for legacy roles; consent has its own Settings destinations. */}
      <Route
        path="compliance"
        element={<CompanyNavigate to="/phone/compliance" replace />}
      />
      {/* Account-level telephony (SD-15879), moved out of the Phone workspace
                  so companies without the Phone System keep number management and
                  A2P registration. */}
      <Route
        path="phone-numbers"
        lazy={lazyImport(() => import("@libs/Account"), "SettingsPhoneNumbers")}
      />
      <Route
        path="texting-registration"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyTextingRegistration",
        )}
      />
      {/* Account-scoped blocked callers (SD-17708). Leaf import, not the
          @libs/Phone barrel, so this settings chunk does not pull the whole
          Phone workspace in behind it. */}
      <Route
        path="blocked-callers"
        lazy={lazyImport(
          () => import("@libs/Phone/blockedCallers/BlockedCallers"),
          "BlockedCallers",
        )}
      />
      <Route
        path="data-privacy"
        lazy={lazyImport(() => import("@libs/Company"), "CompanyDataPrivacy")}
      />
      <Route
        path="inbound-consent"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyInboundConsent",
        )}
      />
      <Route
        path="outbound-consent"
        lazy={lazyImport(
          () => import("@libs/Company"),
          "CompanyOutboundConsent",
        )}
      />
      {/* Export and the former Account -> Advanced page (which held
                  only the product-analytics toggle) merged into Data &
                  Privacy; their URLs redirect. Legacy /company/export and
                  /account/advanced chain here through the splat redirects
                  below. */}
      <Route
        path="export"
        loader={redirectWithinCompany("/settings/data-privacy")}
      />
      <Route
        path="advanced"
        loader={redirectWithinCompany("/settings/data-privacy")}
      />
      {/* Company staff settings moved under /super — see the workspace above. */}
      {/* Former Account section (Phone Numbers + Texting live in the
                  Phone workspace since FE-1). */}
      <Route
        path="team"
        lazy={lazyImport(() => import("@libs/Account"), "AccountTeam")}
      >
        <Route
          path="invite"
          lazy={lazyImport(
            () => import("@libs/Team/components"),
            "InviteUsers",
          )}
        />
      </Route>
      <Route
        path="companies"
        lazy={lazyImport(() => import("@libs/Account"), "AccountCompanies")}
      />
      <Route
        path="billing"
        lazy={lazyImport(() => import("@libs/Account"), "AccountBilling")}
      />
      <Route
        path="notifications"
        lazy={lazyImport(() => import("@libs/Account"), "AccountNotifications")}
      />
      <Route
        path="profile"
        lazy={lazyImport(
          () => import("@libs/UserSettings/SettingsWorkspaceSections"),
          "UserProfileSettings",
        )}
      />
      <Route
        path="security"
        lazy={lazyImport(
          () => import("@libs/UserSettings/SettingsWorkspaceSections"),
          "UserSecuritySettings",
        )}
      />
    </Route>
    {/* Legacy /company/* URLs — the Company workspace merged into Settings.
                Retired staff paths are intentionally not preserved here. */}
    <Route path="company">
      <Route index loader={redirectWithinCompany("/settings")} />
      {/* Consolidated into the Phone workspace (FE-1). */}
      <Route
        path="transferring-numbers"
        element={
          <CompanyNavigate to="/settings/transfer-destinations" replace />
        }
      />
      <Route
        path="text-messaging"
        element={<CompanyNavigate to="/settings/notifications" replace />}
      />
      <Route path="*" loader={redirectSplat("/settings")} />
    </Route>
    <Route
      path="conversations"
      lazy={lazyImport(() => import("@libs/Conversations"), "Conversations")}
    />
    <Route
      path="coach"
      loader={companyDataLoader}
      lazy={lazyImport(() => import("@libs/Coach"), "Coach")}
    >
      <Route
        index
        lazy={lazyImport(() => import("@libs/Coach"), "CoachIndex")}
      />
      <Route
        path="overview"
        lazy={lazyImport(() => import("@libs/Coach"), "CoachOverview")}
      />
      <Route
        path="conversations"
        lazy={lazyImport(() => import("@libs/Coach"), "CoachConversations")}
      />
      <Route
        path="agent-performance"
        lazy={lazyImport(() => import("@libs/Coach"), "CoachAgentPerformance")}
      />
      <Route
        path="team-performance"
        lazy={lazyImport(() => import("@libs/Coach"), "CoachTeamPerformance")}
      />
      <Route
        path="settings"
        lazy={lazyImport(() => import("@libs/Coach"), "CoachConfig")}
      />
    </Route>
    <Route
      path="field-sales"
      loader={companyDataLoader}
      handle={fullWidth}
      lazy={lazyImport(
        () => import("@libs/FieldSales/FieldSalesLayout"),
        "FieldSalesLayout",
      )}
    >
      <Route
        index
        lazy={lazyImport(
          () => import("@libs/FieldSales/FieldSalesDashboard"),
          "FieldSalesDashboard",
        )}
      />
      <Route
        path="map"
        lazy={lazyImport(
          () => import("@libs/FieldSales/FieldSales"),
          "FieldSales",
        )}
      />
      <Route
        path="leaderboards"
        lazy={lazyImport(
          () => import("@libs/FieldSales/Leaderboards"),
          "Leaderboards",
        )}
      />
      <Route
        path="leaderboards/tv"
        lazy={lazyImport(
          () => import("@libs/FieldSales/LeaderboardTv"),
          "LeaderboardTv",
        )}
      />
      <Route
        path="competitions"
        lazy={lazyImport(
          () => import("@libs/FieldSales/Competitions"),
          "Competitions",
        )}
      />
      <Route
        path="settings"
        lazy={lazyImport(
          () => import("@libs/FieldSales/components/FieldSalesSettingsDialog"),
          "FieldSalesSettings",
        )}
      />
    </Route>
    <Route
      path="field-sales/territories/:territoryId"
      loader={companyDataLoader}
      handle={fullWidth}
      lazy={lazyImport(
        () => import("@libs/FieldSales/TerritoryDetail"),
        "TerritoryDetail",
      )}
    />
    <Route
      path="booking-wizard"
      loader={companyDataLoader}
      lazy={lazyImport(() => import("@libs/BookingWizard"), "BookingWizard")}
    />
    <Route
      path="chat-widget"
      loader={companyDataLoader}
      lazy={lazyImport(() => import("@libs/ChatWidget"), "ChatWidget")}
    />
    <Route
      path="integrations"
      lazy={lazyImport(
        () => import("@libs/Integrations/Integrations"),
        "Integrations",
      )}
    >
      <Route
        index
        lazy={lazyImport(
          () => import("@libs/Integrations/IntegrationsOverview"),
          "IntegrationsOverview",
        )}
      />
      <Route
        path="setup"
        lazy={lazyImport(
          () => import("@libs/Integrations/IntegrationSetupLinkPage"),
          "IntegrationSetupLinkPage",
        )}
      />
      <Route
        path="servicetitan"
        lazy={lazyImport(
          () => import("@libs/Integrations/ServiceTitan"),
          "ServiceTitan",
          { keySearchParam: "tab" },
        )}
      />
      <Route
        path="fieldroutes"
        lazy={lazyImport(
          () => import("@libs/Integrations/FieldRoutes"),
          "FieldRoutes",
          { keySearchParam: "tab" },
        )}
      />
      <Route
        path="housecallpro"
        lazy={lazyImport(
          () => import("@libs/Integrations/HouseCallPro"),
          "HouseCallPro",
          { keySearchParam: "tab" },
        )}
      />
      <Route
        path="maidcentral"
        lazy={lazyImport(
          () => import("@libs/Integrations/MaidCentral"),
          "MaidCentral",
          { keySearchParam: "tab" },
        )}
      />
      <Route
        path="servicefusion"
        lazy={lazyImport(
          () => import("@libs/Integrations/ServiceFusion"),
          "ServiceFusion",
          { keySearchParam: "tab" },
        )}
      />
      <Route
        path="thumbtack"
        lazy={lazyImport(
          () => import("@libs/Integrations/Thumbtack"),
          "Thumbtack",
        )}
      />
      <Route
        path="google_calendar"
        lazy={lazyImport(
          () => import("@libs/Integrations/GoogleCalendar"),
          "GoogleCalendar",
        )}
      />
      <Route
        path="outlook_calendar"
        lazy={lazyImport(
          () => import("@libs/Integrations/OutlookCalendar"),
          "OutlookCalendar",
        )}
      />
      <Route
        path="google_lsa"
        lazy={lazyImport(
          () => import("@libs/Integrations/GoogleLsa"),
          "GoogleLsa",
        )}
      />
      <Route
        path="yelp"
        lazy={lazyImport(() => import("@libs/Integrations/Yelp"), "Yelp")}
      />
      <Route
        path="leadperfection"
        lazy={lazyImport(
          () => import("@libs/Integrations/LeadPerfection"),
          "LeadPerfection",
        )}
      />
      <Route
        path="hubspot"
        lazy={lazyImport(() => import("@libs/Integrations/Hubspot"), "Hubspot")}
      />
      <Route
        path="salesforce"
        lazy={lazyImport(
          () => import("@libs/Integrations/Salesforce"),
          "Salesforce",
        )}
      />
      <Route
        path="watsco_oca"
        element={
          <CompanyNavigate
            to="/integrations/salesforce?auth=jwt_bearer"
            replace
          />
        }
      />
      <Route
        path="quickbooks"
        lazy={lazyImport(
          () => import("@libs/Integrations/QuickBooks"),
          "QuickBooks",
        )}
      />
      <Route
        path="social/:platform"
        lazy={lazyImport(
          () => import("@libs/Integrations/social/SocialIntegration"),
          "SocialIntegration",
        )}
      />
      <Route
        path="eight_by_eight"
        lazy={lazyImport(
          () => import("@libs/Integrations/EightByEight"),
          "EightByEight",
        )}
      />
      <Route
        path="voice_for_pest"
        lazy={lazyImport(
          () => import("@libs/Integrations/VoiceForPest"),
          "VoiceForPest",
        )}
      />
      <Route
        path="voice_for_pest_sip"
        lazy={lazyImport(
          () => import("@libs/Integrations/VoiceForPestSip"),
          "VoiceForPestSip",
        )}
      />
      <Route
        path="customcrm"
        lazy={lazyImport(
          () => import("@libs/Integrations/CustomCrm"),
          "CustomCrm",
        )}
      />
    </Route>
    <Route
      path="playbooks/new"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Workflows"), "WorkflowEditor")}
    />
    <Route
      path="playbooks/:id"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Workflows"), "WorkflowEditor")}
    />
    <Route
      path="playbooks/:id/edit"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Workflows"), "WorkflowEditor")}
    />
    <Route
      path="playbooks"
      loader={redirectWithinCompany("/agents/playbooks")}
    />
    <Route
      path="services"
      lazy={lazyImport(() => import("@libs/Services/Services"), "Services")}
    >
      {/* Custom Services + Pricing Lookup moved into the Settings
                  workspace when the standalone Services nav retired (SD-10920).
                  Redirect in the loader (matching the playbooks redirect above)
                  so old bookmarks migrate during the loading phase. */}
      <Route
        index
        loader={redirectWithinCompany("/settings/custom-services")}
      />
      <Route
        path="pricing-lookup"
        loader={redirectWithinCompany("/settings/pricing-lookup")}
      />
      <Route
        path="pricing-catalog"
        lazy={lazyImport(() => import("@libs/Services"), "PricingCatalog")}
      />
      <Route
        path="job-tags"
        lazy={lazyImport(() => import("@libs/Services"), "JobTags")}
      />
      <Route
        path="job-types"
        lazy={lazyImport(() => import("@libs/Services"), "JobTypes")}
      />
      <Route
        path="call-reasons"
        lazy={lazyImport(() => import("@libs/Services"), "CallReasons")}
      />
      <Route
        path="campaigns"
        lazy={lazyImport(
          () => import("@libs/Services"),
          "ServiceTitanCampaigns",
        )}
      />
      <Route
        path="custom-fields"
        lazy={lazyImport(() => import("@libs/Services"), "CustomFields")}
      />
      <Route
        path="membership-types"
        lazy={lazyImport(() => import("@libs/Services"), "MembershipTypes")}
      />
      <Route
        path="business-units"
        lazy={lazyImport(() => import("@libs/Services"), "BusinessUnits")}
      />
      <Route
        path="zones"
        lazy={lazyImport(() => import("@libs/Services"), "Zones")}
      />
      <Route
        path="arrival-windows"
        lazy={lazyImport(() => import("@libs/Services"), "ArrivalWindows")}
      />
      <Route
        path="technicians"
        lazy={lazyImport(() => import("@libs/Services"), "Technicians")}
      />
      <Route
        path="availability"
        lazy={lazyImport(() => import("@libs/Services"), "Availability")}
      />
      <Route
        path="adaptive-capacity"
        lazy={lazyImport(
          () => import("@libs/Services"),
          "AdaptiveCapacityLayout",
        )}
      >
        <Route
          index
          lazy={lazyImport(() => import("@libs/Services"), "AdaptiveCapacity")}
        />
        <Route
          path="rules/new"
          lazy={lazyImport(
            () => import("@libs/Services"),
            "StrategicRuleEditor",
          )}
        />
        <Route
          path="rules/:ruleId"
          lazy={lazyImport(
            () => import("@libs/Services"),
            "StrategicRuleEditor",
          )}
        />
        <Route
          path="adjustments/new"
          lazy={lazyImport(
            () => import("@libs/Services"),
            "ManualAdjustmentEditor",
          )}
        />
        <Route
          path="adjustments/:adjustmentId"
          lazy={lazyImport(
            () => import("@libs/Services"),
            "ManualAdjustmentEditor",
          )}
        />
        <Route
          path="cursor"
          lazy={lazyImport(
            () => import("@libs/Services"),
            "CursorAvailabilityLayout",
          )}
        >
          <Route
            index
            lazy={lazyImport(
              () => import("@libs/Services"),
              "CursorInventoryPage",
            )}
          />
          <Route
            path="jobs"
            lazy={lazyImport(
              () => import("@libs/Services"),
              "CursorSeedJobsPage",
            )}
          />
          <Route
            path="jobs/:jobId"
            lazy={lazyImport(
              () => import("@libs/Services"),
              "CursorSeedJobDetail",
            )}
          />
        </Route>
      </Route>
      <Route
        path="forwarder-rules"
        lazy={lazyImport(() => import("@libs/Services"), "ForwarderRules")}
      />
      <Route
        path="price-book-services"
        lazy={lazyImport(() => import("@libs/Services"), "PriceBookServices")}
      />
      <Route
        path="offices"
        lazy={lazyImport(() => import("@libs/Services"), "Offices")}
      />
    </Route>
    <Route
      path="home"
      element={<CompanyNavigate to="/operator/chat" replace />}
    />
    {/* The short-lived /reporting dashboard merged into /reports. */}
    <Route
      path="reporting"
      element={<CompanyNavigate to="/reports" replace />}
    />
    <Route
      path="notifications"
      lazy={lazyImport(
        () => import("@/components/MainNavigation/NotificationHistoryPage"),
        "NotificationHistoryPage",
      )}
    />
    <Route
      path="activity"
      lazy={lazyImport(() => import("@libs/ActivityFeed"), "ActivityFeed")}
    />
    <Route path="reports">
      <Route
        index
        loader={({ params, request }) =>
          redirect(
            buildCompanyPath(
              params.slug,
              `/reports/${DEFAULT_REPORT_WINDOW_PRESET}${new URL(request.url).search}`,
            ),
          )
        }
      />
      <Route
        path=":preset"
        lazy={lazyImport(() => import("@libs/Reports"), "ReportsRoute")}
      />
    </Route>
    {/* Old report notifications remain durable after the bespoke page is gone:
        they open what the Operator home now opens. */}
    <Route path="health-audits/:isoWeek" element={<OperatorHomeRedirect />} />
    <Route path="health-reports/:isoWeek" element={<OperatorHomeRedirect />} />
    {/* The Operator's pages retired into the chat route. Their links stay
        durable (dale writes Task links into notifications and reports): each
        opens its place in the chat route, and Review stays a page of its
        own. */}
    <Route path="operator">
      <Route index element={<OperatorHomeRedirect />} />
      <Route path="recipes" element={<OperatorRoutinesRedirect />} />
      <Route path="automations" element={<OperatorRoutinesRedirect />} />
      <Route
        path="automations/:automationId"
        element={<OperatorAutomationRedirect />}
      />
      <Route path="tasks/:taskId" element={<OperatorTaskRedirect />} />
      <Route
        path="runs/:runId"
        lazy={lazyImport(() => import("@libs/Operator"), "OperatorRunRedirect")}
      />
      <Route
        path="review"
        lazy={lazyImport(() => import("@libs/Operator"), "OperatorReviewPage")}
      />
    </Route>
    {/* The Operator chat route: the default Home destination, so
        it owns the full page. The routines view and a run's task link render
        the same page, so moving between a chat, the routines and a run keeps
        the rail. */}
    <Route
      path="operator/chat/:chatId?"
      lazy={lazyImport(() => import("@libs/OperatorChat"), "OperatorChatRoute")}
    />
    {/* The routines: the list, or one routine's page on one of its tabs. */}
    <Route
      path="operator/chat/routines/:routineId?/:tab?"
      lazy={lazyImport(() => import("@libs/OperatorChat"), "OperatorChatRoute")}
    />
    <Route
      path="operator/chat/relics/:relicId?"
      lazy={lazyImport(() => import("@libs/OperatorChat"), "OperatorChatRoute")}
    />
    {/* The routines' address before they were called routines: the same
        view, and the same routine, at the new address. */}
    <Route
      path="operator/chat/scheduled/:automationId?"
      element={<OperatorScheduledViewRedirect />}
    />
    {/* A run's task as the routines view links it: the chat page finds the
        Task's conversation and replaces the link with it. */}
    <Route
      path="operator/chat/task/:taskId"
      lazy={lazyImport(() => import("@libs/OperatorChat"), "OperatorChatRoute")}
    />
    <Route path="stats" element={<CompanyNavigate to="/reports" replace />} />
    <Route
      path="user-settings"
      lazy={lazyImport(
        () => import("@libs/UserSettings/UserSettings"),
        "default",
      )}
    />
    <Route
      path="help"
      lazy={lazyImport(() => import("@libs/Help"), "HelpCenter")}
    />
    <Route
      path="dispatch"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Dispatch"), "DispatchBoard")}
    />
    <Route
      path="dispatch/jobs/:jobId"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Dispatch"), "JobRecord")}
    />
    <Route
      path="dispatch/tech/:technicianId"
      lazy={lazyImport(() => import("@libs/Dispatch"), "TechDay")}
    />
    <Route
      path="dispatch/my-day"
      lazy={lazyImport(() => import("@libs/Dispatch"), "TechDay")}
    />
    <Route
      path="dispatch/roster"
      lazy={lazyImport(() => import("@libs/Dispatch"), "Roster")}
    />
    <Route
      path="dispatch/customers"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Dispatch"), "CustomersIndex")}
    />
    <Route
      path="dispatch/customers/:customerId"
      handle={fullWidth}
      lazy={lazyImport(() => import("@libs/Dispatch"), "CustomerProfile")}
    />
    {/* Phone workspace (FE-1): consolidates the existing account/company
                phone features under /phone/*. The moved pages keep their existing
                components (relocated, not rewritten); old URLs redirect below. */}
    <Route path="phone" lazy={lazyImport(() => import("@libs/Phone"), "Phone")}>
      {/* The Overview landing was removed; /phone lands on the live
                  Dashboard. The whole workspace is gated on the `phone`
                  expansion flag inside the Phone shell, so an unentitled company
                  never reaches this redirect. */}
      <Route
        index
        element={<CompanyNavigate to="/phone/dashboard" replace />}
      />
      <Route
        path="dashboard"
        lazy={lazyImport(() => import("@libs/Phone"), "PhoneDashboard")}
      />
      <Route
        path="calls"
        lazy={lazyImport(
          () => import("../integrations/PhoneCalls"),
          "PhoneCalls",
        )}
      />
      <Route
        path="my-settings"
        lazy={lazyImport(() => import("@libs/Phone"), "MyPhoneSettings")}
      />
      <Route
        path="seats"
        lazy={lazyImport(() => import("@libs/Phone"), "PhoneSeats")}
      />
      {/* Where an invited person finishes their own seat (SD-15882). Under
          /phone so its first path segment is already a reserved slug, and
          member-facing like my-settings — see its routeConfig entry, which has
          to name the fail-closed roles explicitly. */}
      <Route
        path="claim-seat"
        lazy={lazyImport(() => import("@libs/Phone"), "ClaimPhoneSeat")}
      />
      <Route
        path="queues"
        lazy={lazyImport(() => import("@libs/Phone"), "PhoneQueues")}
      />
      {/* Recording defaults for the team's own calls. Gateable only since
                    SD-15719 split the AI-voice consent sections and 10DLC
                    registration off this page. */}
      <Route
        path="compliance"
        loader={redirectLegacyPhoneComplianceSection}
        lazy={lazyImport(() => import("@libs/Phone"), "PhoneCompliance")}
      />
      {/* The Phone System's richer view of the account's numbers: seat
                    assignment, outbound caller-ID use, and call flows. The plain
                    inventory lives at /settings/phone-numbers. */}
      <Route
        path="numbers"
        lazy={lazyImport(
          () => import("@libs/Account"),
          "PhoneNumbersAndRouting",
        )}
      />
      <Route
        path="port-orders"
        lazy={lazyImport(() => import("@libs/Phone/PortOrders"), "PortOrders")}
      />
      {/* Visual call flow for one number (SD-CC). The number is E.164 in
                    the path, so it arrives URL-encoded ("+" → %2B). */}
      <Route
        path="numbers/:phoneNumber/flow"
        lazy={lazyImport(() => import("@libs/Phone"), "PhoneCallFlow")}
      />
    </Route>
    {/* Legacy /phone/* URLs that redirect ELSEWHERE. These are deliberately
        siblings of the workspace, not children: the Phone shell swaps its
        <Outlet /> for the teaser when a company lacks the Phone System, which
        would swallow a nested redirect and strand these bookmarks (caught by
        tests/integration/phone/redirects.test.ts). A redirect is not a Phone
        System feature, so it must fire for every company. */}
    <Route
      path="phone/recording"
      element={<CompanyNavigate to="/phone/compliance" replace />}
    />
    {/* Texting Registration moved to Settings (SD-15879); /phone/texting
        chains through the same redirect. */}
    <Route
      path="phone/texting-registration"
      element={<CompanyNavigate to="/settings/texting-registration" replace />}
    />
    <Route
      path="phone/texting"
      element={<CompanyNavigate to="/settings/texting-registration" replace />}
    />
    {/* The short-lived Phone → Notifications tab consolidated into
        Settings → Notifications. */}
    <Route
      path="phone/notifications"
      element={<CompanyNavigate to="/settings/notifications" replace />}
    />
    {/* Legacy Texting page: setup moved to Compliance, team alerts to
        Settings → Notifications. */}
    <Route
      path="phone/text-messaging"
      element={<CompanyNavigate to="/settings/notifications" replace />}
    />
    <Route
      path="phone/forwarding"
      element={<CompanyNavigate to="/settings/transfer-destinations" replace />}
    />
    {/* Legacy /account/* URLs — the Account section merged into the
                Settings workspace (team/billing/advanced/companies live under
                /settings/*). */}
    <Route path="account">
      <Route index loader={redirectWithinCompany("/settings/team")} />
      <Route path="team" loader={redirectWithinCompany("/settings/team")} />
      {/* Both land in Settings (SD-15879). /phone/numbers is inside the
                  gated workspace now, so pointing this legacy alias there would
                  show an unentitled company the teaser instead of the number
                  inventory it still has access to. */}
      <Route
        path="phone-numbers"
        element={<CompanyNavigate to="/settings/phone-numbers" replace />}
      />
      <Route
        path="texting"
        element={
          <CompanyNavigate to="/settings/texting-registration" replace />
        }
      />
      <Route path="*" loader={redirectSplat("/settings")} />
    </Route>
    <Route
      path="team"
      lazy={lazyImport(() => import("@libs/Team/Team"), "Team")}
    >
      <Route
        path="invite"
        lazy={lazyImport(() => import("@libs/Team/components"), "InviteUsers")}
      />
    </Route>
    <Route
      path="campaigns"
      loader={companyDataLoader}
      lazy={lazyImport(() => import("@libs/Campaigns"), "Campaigns")}
    >
      <Route
        index
        lazy={lazyImport(() => import("@libs/Campaigns"), "CampaignsPage")}
      />
      <Route
        path="active"
        lazy={lazyImport(() => import("@libs/Campaigns"), "CampaignsPage")}
      />
      <Route
        path="inactive"
        lazy={lazyImport(() => import("@libs/Campaigns"), "CampaignsPage")}
      />
      <Route
        path="new"
        handle={fullWidth}
        lazy={lazyImport(() => import("@libs/Campaigns"), "CampaignEditor")}
      />
      <Route
        path=":id"
        handle={fullWidth}
        lazy={lazyImport(() => import("@libs/Campaigns"), "CampaignEditor")}
      />
    </Route>
  </Route>
);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route
        path="/"
        errorElement={<RootErrorBoundary />}
        lazy={lazyImport(() => import("@libs/Login/Login"), "Login")}
      />
      <Route
        path="invitation"
        lazy={lazyImport(
          () => import("@libs/Invitation/Invitation"),
          "Invitation",
        )}
      />
      <Route
        path={loginRouteManifest.path}
        lazy={lazyImport(
          loginRouteManifest.load,
          loginRouteManifest.componentName,
        )}
      />
      <Route
        path="service-portal"
        lazy={lazyImport(
          () => import("@libs/Dispatch/portal/CustomerPortal"),
          "CustomerPortal",
        )}
      />
      <Route
        path="reset-password"
        lazy={lazyImport(
          () => import("@libs/PasswordReset/PasswordReset"),
          "PasswordReset",
        )}
      />
      <Route
        path="partner-invitation"
        lazy={lazyImport(
          () => import("@libs/AngiPartner/PartnerInvitation"),
          "PartnerInvitation",
        )}
      />
      <Route
        path="partner-reporting"
        loader={authLoader}
        lazy={lazyImport(
          () => import("@libs/AngiPartner/PartnerReporting"),
          "PartnerReporting",
        )}
      />
      <Route
        path="super/partners"
        loader={authLoader}
        lazy={lazyImport(
          () => import("@libs/AngiPartner/PartnerAdministration"),
          "PartnerAdministration",
        )}
      />
      {/* Synthetic meeting preview only; no customer data or partner access. */}
      <Route
        path="preview/angi"
        lazy={lazyImport(
          () => import("@libs/AngiPartner/AngiPartnerDemo"),
          "AngiPartnerDemo",
        )}
      />
      {/* The buyer's quote (SD-17731). Public and chrome-free: the recipient
          has no account, and the share token in the path is the only
          credential dale accepts. Declared in `routeConfig` too — `authLoader`
          reads that one, and a route public in only one of the pair still
          redirects an unauthenticated visitor to login. */}
      <Route
        path="q/:token"
        lazy={lazyImport(
          () => import("@libs/PublicQuote/PublicQuote"),
          "PublicQuote",
        )}
      />
      <Route
        path="q/:token/onboarding"
        lazy={lazyImport(
          () => import("@libs/PublicQuote/PublicQuoteOnboarding"),
          "PublicQuoteOnboarding",
        )}
      />
      {/* HubSpot native-calling provider (SD-15309). Public + chrome-free: the
          same URL is framed by HubSpot as both the anchored remote and the
          detached window, each carrying its own scoped Sameday identity. Sits
          OUTSIDE the auth gate and AuthenticatedShell so it never pulls in the
          app nav/softphone/telephony chrome. Framing for /hubspot/* is relaxed
          to HubSpot origins only at the CloudFront edge (terraform/cds). */}
      <Route
        path="hubspot/provider"
        lazy={lazyImport(
          () => import("@libs/HubSpotCalling/pages"),
          "HubSpotProviderRoute",
        )}
      />
      <Route
        path="hubspot/provider/authorize"
        lazy={lazyImport(
          () => import("@libs/HubSpotCalling/pages"),
          "HubSpotAuthorizeRoute",
        )}
      />
      {/* Auth gate — redirects unauthenticated users */}
      <Route
        errorElement={<AuthErrorBoundary />}
        loader={authLoader}
        lazy={lazyImport(
          () => import("@/providers/AiResponseRatingProvider"),
          "AuthenticatedAiResponseRatingBoundary",
        )}
      >
        {/* Two mounts of the same authenticated tree (SD-11897/SD-12120):
            - unprefixed (legacy): the active company still comes from
              localStorage for customer routes. Staff routes are excluded from
              this compatibility mount and live only under /super. A later PR
              retires the mount once localStorage is dropped as the company
              source of truth.
            - bare-root `/:slug`: the slug loader resolves the URL slug to the
              active company and seeds session.companyId before the shell
            paints. Static routes outrank the `:slug` param. */}
        <Route
          lazy={lazyImport(
            () => import("@/layouts/AuthenticatedShell"),
            "AuthenticatedShell",
          )}
        >
          {renderAuthenticatedRoutes()}
        </Route>
        <Route
          path=":slug"
          loader={companySlugLoader}
          lazy={lazyImport(
            () => import("@/layouts/AuthenticatedShell"),
            "AuthenticatedShell",
          )}
        >
          {/* The bare company root has no page of its own — redirect to the
              role default landing under the slug (avoids an empty shell). */}
          <Route index loader={companyRootRedirect} />
          {renderAuthenticatedRoutes()}
        </Route>
      </Route>
    </Route>,
  ),
  {
    // Vite's base path ("/" for normal builds, "/pr-N/" for per-PR preview
    // deploys — SD-10588) so routes match and links resolve under a subpath.
    basename: import.meta.env.BASE_URL,
  },
);
